/**
 * Review Incentives API (Phase 9)
 *
 * Management-UI-only endpoints. Covers both user-side review submission/redemption
 * and admin-side review approval. Widget uses widgetSubscriptionApi for read-only
 * access per Phase 9 Option C.
 */

import apiService from './api';
import {
  LifetimeReviewSubmission,
  LifetimeReviewCredit,
  ReviewPlatform,
  ReviewRejectionType,
  ReviewStatus,
  CanSubmitInfo,
  EligibleWorkspace,
} from '../widget/types';

export interface GetAllReviewsFilters {
  status?: ReviewStatus;
  platform?: ReviewPlatform;
  limit?: number;
  offset?: number;
}

export interface RedeemCreditResponse {
  success: boolean;
  workspace_addon_id: number;
  credit: LifetimeReviewCredit;
}

export interface ApprovalResponse {
  submission: LifetimeReviewSubmission;
  credit: LifetimeReviewCredit | null;
}

export const reviewIncentivesApi = {
  // ==================== USER ENDPOINTS (/api/review-incentives/*) ====================

  async getMyCredits(): Promise<LifetimeReviewCredit[]> {
    const response = await apiService.post('/review-incentives/my-credits', {});
    return response.data.credits || [];
  },

  async getMySubmissions(): Promise<LifetimeReviewSubmission[]> {
    const response = await apiService.post('/review-incentives/my-submissions', {});
    return response.data.submissions || [];
  },

  async canSubmit(): Promise<CanSubmitInfo> {
    const response = await apiService.post('/review-incentives/can-submit', {});
    return response.data;
  },

  async submit(
    platform: ReviewPlatform,
    reviewUrl: string,
    screenshotUrl?: string
  ): Promise<LifetimeReviewSubmission> {
    const response = await apiService.post('/review-incentives/submit', {
      platform,
      review_url: reviewUrl,
      screenshot_url: screenshotUrl || null,
    });
    return response.data.submission;
  },

  async getMyEligibleWorkspaces(): Promise<EligibleWorkspace[]> {
    const response = await apiService.post('/review-incentives/my-eligible-workspaces', {});
    return response.data.workspaces || [];
  },

  async redeemCredit(
    creditId: number,
    targetWorkspaceId: number
  ): Promise<RedeemCreditResponse> {
    const response = await apiService.post('/review-incentives/redeem', {
      credit_id: creditId,
      target_workspace_id: targetWorkspaceId,
    });
    return response.data;
  },

  // ==================== ADMIN ENDPOINTS (/api/lifetime-reviews/*) ====================

  async getPendingReviews(): Promise<LifetimeReviewSubmission[]> {
    const response = await apiService.get('/lifetime-reviews/pending');
    return response.data.submissions || [];
  },

  async getAllReviews(
    filters: GetAllReviewsFilters = {}
  ): Promise<LifetimeReviewSubmission[]> {
    const queryParams = new URLSearchParams();
    if (filters.status) queryParams.append('status', filters.status);
    if (filters.platform) queryParams.append('platform', filters.platform);
    if (filters.limit) queryParams.append('limit', filters.limit.toString());
    if (filters.offset) queryParams.append('offset', filters.offset.toString());

    const query = queryParams.toString();
    const response = await apiService.get(
      `/lifetime-reviews/all${query ? `?${query}` : ''}`
    );
    return response.data.submissions || [];
  },

  async approveReview(id: number): Promise<ApprovalResponse> {
    const response = await apiService.post(`/lifetime-reviews/${id}/approve`, {});
    return response.data;
  },

  async rejectReview(
    id: number,
    options: {
      rejection_type: ReviewRejectionType;
      rejection_reason?: string;
    }
  ): Promise<LifetimeReviewSubmission> {
    const response = await apiService.post(`/lifetime-reviews/${id}/reject`, {
      rejection_type: options.rejection_type,
      rejection_reason: options.rejection_reason || null,
    });
    return response.data.submission;
  },
};

export default reviewIncentivesApi;
